import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  LocalHospital as LocalHospitalIcon,
  AttachMoney as AttachMoneyIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { fetchBillById } from '../../redux/thunks/billingThunks';
import { useUserData } from '../../hooks/useUserData';

// Styled Components - Consistent with other pages
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const CompactButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '8px 16px',
  minWidth: 'auto',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  '& .MuiTableCell-body': {
    fontSize: '0.875rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ClaimDetails = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: userData } = useUserData();
  const { currentBill, loading: billLoading, error: billError } = useSelector(state => state.billing);
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract bill ID from claim ID (claim IDs are formatted as "claim_<billId>")
  const billId = claimId?.startsWith('claim_') ? claimId.replace('claim_', '') : claimId;

  const loadClaimDetails = useCallback(() => {
    setLoading(true);
    setError(null);
    if (userData) {
      const userDataForThunk = {
        id: userData.id || userData.uid,
        email: userData.email,
        roles: userData.roles,
        name: userData.name || userData.displayName,
        defaultFacility: userData.defaultFacility
      };
      dispatch(fetchBillById(billId, userDataForThunk));
    }
  }, [billId, userData, dispatch]);

  useEffect(() => {
    if (billId && userData) {
      loadClaimDetails();
    }
  }, [billId, userData, loadClaimDetails]);

  // Convert bill to claim format when bill is loaded
  useEffect(() => {
    if (currentBill && currentBill.id === billId) {
      const claimData = {
        id: claimId,
        claimNumber: `CLM${currentBill.billNumber || currentBill.id}`,
        billId: currentBill.id,
        patientName: currentBill.patientName,
        patientId: currentBill.patientId,
        patientMRN: currentBill.patientMRN,
        totalAmount: currentBill.totalAmount,
        paidAmount: currentBill.paidAmount || 0,
        status: currentBill.claimStatus || currentBill.status || 'pending',
        clearinghouse: currentBill.clearinghouse || 'N/A',
        submissionDate: currentBill.claimSubmissionDate || currentBill.createdAt,
        responseDate: currentBill.claimResponseDate,
        createdAt: currentBill.createdAt,
        denialReason: currentBill.denialReason,
        denialCode: currentBill.denialCode,
        resubmissionCount: currentBill.resubmissionCount || 0,
        insuranceName: currentBill.insuranceName || currentBill.payerName,
        serviceLines: currentBill.items?.map(item => ({
          code: item.cptCode || item.code || 'N/A',
          description: item.description || item.name,
          units: item.quantity || 1,
          charge: item.amount || item.price || 0
        })) || [],
        allowedAmount: currentBill.allowedAmount || 0,
        patientResponsibility: currentBill.patientResponsibility || 
          (currentBill.totalAmount - (currentBill.paidAmount || 0))
      };
      setClaim(claimData);
      setLoading(false);
    }
  }, [currentBill, billId, claimId]);

  // Handle loading state from Redux
  useEffect(() => {
    if (!billLoading && !currentBill && !loading) {
      setError('Claim not found');
    }
  }, [billLoading, currentBill, loading]);

  // Handle error from Redux
  useEffect(() => {
    if (billError) {
      setError(billError);
      setLoading(false);
    }
  }, [billError]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'paid':
        return 'success';
      case 'denied':
      case 'rejected':
        return 'error';
      case 'submitted':
        return 'info';
      case 'processing':
        return 'warning';
      case 'pending':
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'paid':
        return <CheckCircleIcon />;
      case 'denied':
      case 'rejected':
        return <ErrorIcon />;
      case 'submitted':
        return <SendIcon />;
      case 'processing':
        return <ScheduleIcon />;
      case 'pending':
      default:
        return <AssignmentIcon />;
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <HeaderCard>
          <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white' }}>
                Claim Details
              </Typography>
            </Box>
          </CardContent>
        </HeaderCard>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!claim) {
    return (
      <Box sx={{ p: 3 }}>
        <HeaderCard>
          <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white' }}>
                Claim Details
              </Typography>
            </Box>
          </CardContent>
        </HeaderCard>
        <Alert severity="warning">Claim not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Card - Consistent with BillDetails */}
      <HeaderCard>
        <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={3} alignItems="center" justifyContent="space-between">
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{ color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                    Claim Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    #{claim.claimNumber || claimId} • {claim.patientName}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  icon={getStatusIcon(claim.status)}
                  label={claim.status?.toUpperCase() || 'PENDING'}
                  color={getStatusColor(claim.status)}
                  sx={{ 
                    fontWeight: 600,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '& .MuiChip-icon': { color: 'inherit' }
                  }}
                />
                <Tooltip title="Refresh">
                  <CompactButton
                    startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
                    onClick={loadClaimDetails}
                  >
                    Refresh
                  </CompactButton>
                </Tooltip>
                <Tooltip title="Print Claim">
                  <CompactButton
                    startIcon={<PrintIcon sx={{ fontSize: 18 }} />}
                    onClick={() => window.print()}
                  >
                    Print
                  </CompactButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </HeaderCard>

      {/* Summary Cards Row - Compact */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <InfoCard>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '8px', 
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AccountBalanceIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                    Total Amount
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
                    {formatCurrency(claim.totalAmount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <InfoCard>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '8px', 
                  backgroundColor: 'success.light',
                  color: 'success.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                    Paid Amount
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', lineHeight: 1.2 }}>
                    {formatCurrency(claim.paidAmount || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <InfoCard>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: '8px', 
                  backgroundColor: claim.patientResponsibility > 0 ? 'warning.light' : 'success.light',
                  color: claim.patientResponsibility > 0 ? 'warning.contrastText' : 'success.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PaymentIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                    Patient Responsibility
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: claim.patientResponsibility > 0 ? 'warning.main' : 'success.main', lineHeight: 1.2 }}>
                    {formatCurrency(claim.patientResponsibility || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Claim Summary */}
        <Grid item xs={12} md={8}>
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon sx={{ mr: 1 }} />
                Claim Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Claim Number</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {claim.claimNumber || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Bill ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {claim.billId || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Clearinghouse</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {claim.clearinghouse || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">Resubmission Count</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {claim.resubmissionCount || 0}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </InfoCard>

          {/* Patient Information */}
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1 }} />
                Patient Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">Patient Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {claim.patientName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">Patient MRN</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {claim.patientMRN || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">Insurance</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {claim.insuranceName || claim.payerName || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </InfoCard>

          {/* Service Lines */}
          {claim.serviceLines && claim.serviceLines.length > 0 && (
            <InfoCard sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <LocalHospitalIcon sx={{ mr: 1 }} />
                  Service Lines
                </Typography>
                <TableContainer>
                  <StyledTable size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Units</TableCell>
                        <TableCell align="right">Charge</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {claim.serviceLines.map((line, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{line.code}</TableCell>
                          <TableCell>{line.description}</TableCell>
                          <TableCell align="right">{line.units || 1}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(line.charge)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid #eee', borderBottom: 'none' } }}>
                        <TableCell colSpan={3} align="right">Total</TableCell>
                        <TableCell align="right" sx={{ color: 'primary.main' }}>
                          {formatCurrency(claim.serviceLines.reduce((sum, line) => sum + (line.charge || 0), 0))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </StyledTable>
                </TableContainer>
              </CardContent>
            </InfoCard>
          )}

          {/* Denial Information */}
          {claim.status === 'denied' && claim.denialReason && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Denial Reason</Typography>
              <Typography variant="body2">{claim.denialReason}</Typography>
              {claim.denialCode && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Denial Code: {claim.denialCode}
                </Typography>
              )}
            </Alert>
          )}
        </Grid>

        {/* Right Column - Timeline & Actions */}
        <Grid item xs={12} md={4}>
          {/* Dates Card */}
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon sx={{ mr: 1 }} />
                Timeline
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Created</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(claim.createdAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Submitted</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(claim.submissionDate)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Response Received</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(claim.responseDate)}</Typography>
                </Box>
                {claim.paidDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Payment Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(claim.paidDate)}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </InfoCard>

          {/* Payment Info */}
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <AttachMoneyIcon sx={{ mr: 1 }} />
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Billed Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatCurrency(claim.totalAmount)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Allowed Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatCurrency(claim.allowedAmount || 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                    {formatCurrency(claim.paidAmount || 0)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Patient Responsibility</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatCurrency(claim.patientResponsibility || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </InfoCard>

          {/* Resubmission Info */}
          {claim.resubmissionCount > 0 && (
            <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
              This claim has been resubmitted {claim.resubmissionCount} time(s).
            </Alert>
          )}

          {/* Actions */}
          <InfoCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Actions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {claim.billId && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<ReceiptIcon />}
                    onClick={() => navigate(`/billing/${claim.billId}`)}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 500 }}
                  >
                    View Original Bill
                  </Button>
                )}
                {claim.status === 'denied' && (
                  <Button
                    variant="contained"
                    color="warning"
                    fullWidth
                    startIcon={<RefreshIcon />}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 500 }}
                  >
                    Resubmit Claim
                  </Button>
                )}
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClaimDetails;

